import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../UserContext';
import { useParams, useNavigate } from 'react-router-dom';
import styles from "./Keepers.module.css";
import KeepersTable from "../components/KeepersTable";
import PrizeNavigation from "../components/PrizeNavigation";
import Button from "../components/Button";
import EditForm from '../components/EditForm';
import Modal from '../components/Modal';
import useUserRole from '../hooks/useUserRole';
import { useLeague } from '../utils/LeagueContext';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import NoLeagueMessage from '../components/NoLeagueMessage';
import useLeagueAccessGuard from '../hooks/useLeagueAccessGuard';
import API_BASE_URL from '../utils/apiConfig';


const Keepers = () => {
  const navigate = useNavigate();
  const { leagueId: contextLeagueId, setLeague, selectedLeague } = useLeague();
  const { leagueId: leagueIdFromURL } = useParams();
  const leagueId = selectedLeague ? selectedLeague._id : leagueIdFromURL || null;
  const { user } = useUser();
  const [showForm, setShowForm] = useState(false);
  const [keepers, setKeepers] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const userRole = useUserRole(selectedLeague);
  const [selectedKeeper, setSelectedKeeper] = useState(null); // Track the keeper being edited
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  useLeagueAccessGuard(); // Automatically handles auth, sets league, validates membership

  // Enrich the keeper data with manager information
  const enrichKeeperWithManager = (keeperData, selectedLeague) => {
    const manager =
      selectedLeague?.members?.find(
        (member) =>
          member._id === keeperData.teamManager || member.userId === keeperData.teamManager
      );
  
    return {
      ...keeperData,
      teamManager: {
        _id: manager?._id || keeperData.teamManager,
        userName: manager?.name || 'Unknown Manager',
      },
    };
  };

  // Memoize the keeperFields array to prevent unnecessary re-renders
  const keeperFields = useMemo(() => {
    const members = selectedLeague?.members || [];
    return [
      {
        name: 'actions',
        label: 'Actions',
        render: (keeper) => {
          return (
            <IconButton
              aria-label="Edit Keeper"
              size="small"
              className={styles.editIcon}
              onClick={() => handleEditKeeper(keeper)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          );
        },
      },      
      { name: 'player', label: 'Player', type: 'text', placeholder: 'Enter Player Name', required: true },
      { name: 'yearAcquired', label: 'Year Acquired', type: 'text', placeholder: 'Enter Year', required: true },
      { name: 'acqCost', label: 'Acq Cost', type: 'text', placeholder: 'Enter Acquisition Cost', required: true },
      { name: 'keeperYear', label: 'Keeper Year', type: 'text', placeholder: 'Enter Keeper Year', required: true },
      { name: 'keeperCost', label: 'Keeper Cost', type: 'text', placeholder: 'Enter Keeper Cost', required: true },
    ];
  }, [selectedLeague?.members]);

  // 📝 Fields used in the form
  // Memoize the keeperFields array to prevent unnecessary re-renders
  const formFields = useMemo(() => {
    const members = selectedLeague?.members || [];
    return [
      { 
        name: 'manager', 
        label: 'Manager', 
        type: 'select', 
        options: members.map(member => ({ label: member.name, value: member.userId })), 
        placeholder: 'Select Manager', 
        required: true 
      },
      { name: 'player', label: 'Player', type: 'text', placeholder: 'Enter Player Name', required: true },
      { name: 'yearAcquired', label: 'Year Acquired', type: 'text', placeholder: 'Enter Year', required: true },
      { name: 'acqCost', label: 'Acq Cost', type: 'text', placeholder: 'Enter Acquisition Cost', required: true },
      { name: 'keeperYear', label: 'Keeper Year', type: 'text', placeholder: 'Enter Keeper Year', required: true },
      { name: 'keeperCost', label: 'Keeper Cost', type: 'text', placeholder: 'Enter Keeper Cost', required: true },
    ];
  }, [selectedLeague?.members]);

  const [formKey, setFormKey] = useState(0);

  const closeModal = () => {
    setShowForm(false);
    setSelectedKeeper(null);
    setFormKey(prev => prev + 1); // Force EditForm to reset
  };
  
  // Debug logs
  useEffect(() => {
  }, [leagueIdFromURL, leagueId]);

  // If the URL does not include leagueId but context has it, update the URL.
  useEffect(() => {
    if (selectedLeague && selectedLeague._id && !leagueIdFromURL) {
      navigate(`/keepers/${selectedLeague._id}`, { replace: true });
    }
  }, [selectedLeague, leagueIdFromURL, navigate]);

  // (Optional) You can validate the token here or assume it’s already validated.

  // Fetch keeper data for the league
  const fetchKeepers = async () => {
    if (!leagueId || !user?.token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/keepers/${leagueId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch keepers. Status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // Map teamManager IDs to full objects using selectedLeague.members
        const updatedKeepers = data.map(keeper => ({
          ...keeper,
          teamManager: selectedLeague?.members?.find(
            member => member._id === keeper.teamManager
        ) || { userId: keeper.teamManager, userName: 'Unknown Manager' },
      }));
      setKeepers(updatedKeepers);
        setKeepers(data);
      } else {
        console.error('API response is not an array:', data);
      }
    } catch (error) {
      console.error('Error fetching keepers:', error);
    }
  };

  useEffect(() => {
    fetchKeepers();
  }, [leagueId, user?.token, isAuthenticated]);

  // (Optional) If you want to re-fetch league details in the Keepers page in case context is missing,
  // you can do that here. However, ideally the LeagueContext (at a higher level) should be in charge of fetching.
  const fetchLeagueDetails = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/leagues/${id}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch league details. Status: ${response.statusText}`);
      }
      const data = await response.json();
    } catch (error) {
      console.error('Error fetching league details:', error);
    }
  };

  // If leagueId is defined, try to fetch league details.
  useEffect(() => {
    if (leagueId) {
      fetchLeagueDetails(leagueId);
    }
  }, [leagueId, user.token]);

  // Define columns and fields for the table and form (unchanged)
  const keeperColumns = useMemo(() => { // Memoize to prevent unnecessary re-renders and run only when userRole is defined
    if (!userRole) return [];
  
    return [
      userRole === 'commissioner' && {
        name: 'actions',
        label: 'Actions',
        render: (keeper) => (
          <IconButton
            aria-label="Edit Keeper"
            size="small"
            className={styles.editIcon}
            onClick={() => handleEditKeeper(keeper)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      { name: 'teamManager', label: 'Team Manager' },
      { name: 'player', label: 'Player' },
      { name: 'yearAcquired', label: 'Year Acquired' },
      { name: 'acquisitionCost', label: 'Acq Cost' },
      { name: 'keeperYear', label: 'Keeper Yr' },
      { name: 'keeperCost', label: 'Keeper Cost' },
    ].filter(Boolean);
  }, [userRole]);

  // Handle form submission for adding or editing a keeper
  const handleFormSubmit = async (formData) => {
    try {
      const keeperPayload = {
        teamManager: formData.manager,
        player: formData.player,
        yearAcquired: formData.yearAcquired,
        acquisitionCost: formData.acqCost,
        keeperYear: formData.keeperYear,
        keeperCost: formData.keeperCost,
        league: leagueId
      };
  
      let response;
      let keeperData;
  
      if (selectedKeeper) {
        // Update existing keeper
        response = await fetch(`${API_BASE_URL}/api/keepers/${selectedKeeper._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          },
          body: JSON.stringify(keeperPayload)
        });
  
        if (!response.ok) {
          throw new Error('Failed to update keeper');
        }
  
        keeperData = await response.json();

        const updatedManager = selectedLeague?.members?.find(
          (member) => member.userId === keeperData.teamManager
        );
        
        const updatedKeeper = enrichKeeperWithManager(keeperData, selectedLeague);

        setKeepers((prevKeepers) =>
          prevKeepers.map((keeper) =>
            keeper._id === updatedKeeper._id ? updatedKeeper : keeper
          )
        );
      } else {
        // Add new keeper
        response = await fetch(`${API_BASE_URL}/api/keepers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          },
          body: JSON.stringify(keeperPayload)
        });
  
        if (!response.ok) {
          throw new Error('Failed to add keeper');
        }
  
        keeperData = await response.json();
  
        const updatedManager = selectedLeague?.members?.find(
          (member) => member._id === keeperData.teamManager
        );
  
        const newKeeper = enrichKeeperWithManager(keeperData, selectedLeague);
        setKeepers((prevKeepers) => [...prevKeepers, newKeeper]);
      }
  
      // Reset form state
      setShowForm(false);
      setSelectedKeeper(null);
    } catch (error) {
      console.error('Error saving keeper:', error);
      alert('Oops. There was an error saving the keeper. Please try again.');
    }
  };
  
  const handleAddKeeper = () => {
    setSelectedKeeper(null);
    setShowForm(true);
    setIsEditing(false);  
  };

  const handleEditKeeper = (keeper) => {
    const managerValue = keeper.teamManager?._id || '';
    const newInitialValues = {
      manager: managerValue,
      player: keeper.player,
      yearAcquired: keeper.yearAcquired,
      acqCost: keeper.acquisitionCost,
      keeperYear: keeper.keeperYear,
      keeperCost: keeper.keeperCost,
    };
  
    // 🧠 Set form values *before* showing the form
    setInitialValues(newInitialValues);
    setSelectedKeeper(keeper);
    setIsEditing(true);
  
    // ✨ Add a short delay to allow dropdown options to populate
    setTimeout(() => {
      setShowForm(true);
    }, 0);
  };
  
  
  const handleDeleteKeeper = async () => { // Delete the selected keeper
    if (!selectedKeeper) {
      console.error('No keeper selected for deletion.');
      return;
    }
  
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this keeper record for player "${selectedKeeper.player}"?`
    );
    if (!confirmDelete) return;
  
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/keepers/${selectedKeeper._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
          },
        }
      );
  
      if (!response.ok) {
        throw new Error('Error deleting keeper');
      }
  
      // Remove the deleted keeper from the state
      setKeepers((prevKeepers) =>
        prevKeepers.filter((keeper) => keeper._id !== selectedKeeper._id)
      );
  
      // Close modal and reset state
      setShowForm(false);
      setSelectedKeeper(null);
    } catch (error) {
      console.error('Error deleting keeper:', error);
      alert('Oops, there was a problem deleting the keeper. Please try again.');
    }
  };
  
  return (
    <>
      <div>
        <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={leagueId} />
      </div>
      <div className={styles.keepersHeader}>

        {!selectedLeague && (
          <NoLeagueMessage entity="keepers" className={styles.leagueName} />
        )}
        <p className={styles.leagueName}>
          {selectedLeague ? selectedLeague.leagueName : ""}
        </p>
        <h3 className={styles.keeperTitle}>Keepers</h3>
        <p>Track your league's keepers</p>
        {userRole === 'commissioner' && (
          <div className={styles.addKeeper}>
            <Button
              label={"Add keeper"}
              onClick={handleAddKeeper}
            />
          </div>
        )}
      </div>

      {userRole && ( // Render the table only if userRole is defined
        <KeepersTable data={keepers} columns={keeperColumns} />
      )}


      {showForm && ( 
        <Modal onClose={closeModal}>
          <EditForm
            context="keepers"
            selectedEntry={selectedKeeper}
            currentLeagueId={leagueId}
            key={formKey}
            title={selectedKeeper ? "Edit Keeper" : "Add Keeper"}
            fields={formFields}
            onCancel={closeModal}
            onSubmit={handleFormSubmit}
            initialValues={initialValues || {}}
            isEditing={isEditing}
            selectedKeeper={selectedKeeper}
          />

          {/* Show the Delete button only when editing an existing prize */}
            {selectedKeeper && (
              <div
                className={styles.deleteKeeper}
                onClick={handleDeleteKeeper}
              >
                Delete
              </div>
            )}
        </Modal>
      )}
    </>
  );
};

export default Keepers;
